import React, { useState } from 'react';
import { Paper, Box, Typography, TextField, Button, CircularProgress, Alert } from '@mui/material';
import {
  StorefrontRounded, CheckCircleRounded, GroupRounded, ViewAgendaRounded, PaymentsRounded,
  WorkspacePremiumRounded, ScheduleRounded, PersonRounded, CloseRounded, ViewListRounded,
  PersonOffRounded, FileDownloadRounded, EventAvailableRounded, DirectionsWalkRounded,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { FilterBar, Field, Spacer, SectionCard, Grid } from '../../components/common';
import StatCard from '../../components/StatCard';
import StatusChip from '../../components/StatusChip';
import DataTable, { Column } from '../../components/DataTable';
import { Avatar } from '../../components/NameCell';
import { LineChart, DoughnutChart, ChartLegend } from '../../components/Charts';
import { useDashboard, DashboardData } from '../../hooks/useDashboard';
import { money, num } from '../../utils/format';
import { chartColors, colors } from '../../theme/theme';

const liveTiles: Array<[keyof DashboardData['live_overview'], string, string, React.ReactElement]> = [
  ['waiting', 'Waiting', '#60A5FA', <ScheduleRounded />],
  ['serving', 'Serving', '#10B981', <PersonRounded />],
  ['completed', 'Completed', '#16A34A', <CheckCircleRounded />],
  ['skipped', 'Skipped', '#F59E0B', <ViewAgendaRounded />],
  ['cancelled', 'Cancelled', '#EF4444', <CloseRounded />],
  ['not_attended', 'Not Attended', '#94A3B8', <PersonOffRounded />],
];

// Token-type split — an orthogonal dimension (every token is one of these two),
// so these counts overlap the status tiles rather than adding to them.
const typeTiles: Array<[keyof DashboardData['live_overview'], string, string, React.ReactElement]> = [
  ['pre_booked', 'Pre-booked', '#6366F1', <EventAvailableRounded />],
  ['walk_in', 'Walk-in', '#0EA5E9', <DirectionsWalkRounded />],
];

const activityIcon: Record<string, { icon: React.ReactElement; color: string }> = {
  business: { icon: <StorefrontRounded />, color: '#076FF7' },
  subscription: { icon: <WorkspacePremiumRounded />, color: '#EC4899' },
  token: { icon: <ViewAgendaRounded />, color: '#F59E0B' },
};

const reasonStatus = (reason: string) =>
  /fail/i.test(reason) ? 'failed' : /paus|inactive|closed/i.test(reason) ? 'paused' : 'warning';

/** Build a StatCard trend chip from a signed percentage. */
const trend = (p: number) => ({ dir: (p < 0 ? 'down' : 'up') as 'up' | 'down', text: `${Math.abs(p)}% vs prev` });

/** Local (not UTC) YYYY-MM-DD so the default range matches the server's CURRENT_DATE in IST. */
const todayStr = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const AdminDashboard: React.FC = () => {
  const today = todayStr();
  const [from, setFrom] = useState<string>(today);
  const [to, setTo] = useState<string>(today);
  const { data, loading, error, reload } = useDashboard(from, to);

  const isToday = from === today && to === today;
  const rangeText = isToday ? 'Today' : from === to ? from : `${from} → ${to}`;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !data) {
    return (
      <Alert severity="error" action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}>
        {error || 'No dashboard data.'}
      </Alert>
    );
  }

  const { kpis, trends, live_overview, subscription_status, charts, top_businesses, recent_activities, pending_activations, inactive } = data;
  const ss = subscription_status;
  const catColors = charts.top_categories.map((_, i) => chartColors[i % chartColors.length]);

  const pct = (v: number) => (ss.total ? Math.round((v / ss.total) * 100) : 0);

  /** Client-side CSV export of the current dashboard snapshot. */
  const exportCsv = (): void => {
    const lines: Array<[string, string | number]> = [
      ['Metric', 'Value'],
      ['Range', `${from} to ${to}`],
      ['Total Businesses', kpis.total_businesses],
      ['Active Businesses', kpis.active_businesses],
      ['Total Users', kpis.total_users],
      ['Total Queues', kpis.total_queues],
      ['Tokens (range)', kpis.todays_tokens],
      ['Revenue (range)', kpis.monthly_revenue],
      ['Active Subscriptions', kpis.active_subscriptions],
      ...liveTiles.map(([key, label]) => [`Live · ${label}`, live_overview[key]] as [string, number]),
      ...typeTiles.map(([key, label]) => [`Type · ${label}`, live_overview[key]] as [string, number]),
      ['Subs · Active', ss.active], ['Subs · Trial', ss.trial], ['Subs · Expired', ss.expired], ['Subs · Cancelled', ss.cancelled],
    ];
    const csv = lines.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `dashboard-${from}_to_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const pendingCols: Column<DashboardData['pending_activations'][number]>[] = [
    { key: 'queue', label: 'Queue', render: (r) => <Typography component="span" sx={{ fontWeight: 600 }}>{r.queue}</Typography> },
    { key: 'business', label: 'Business' },
    { key: 'submitted', label: 'Submitted' },
    { key: 'status', label: 'Status', render: (r) => <StatusChip status="pending" label={r.status.replace(/_/g, ' ')} /> },
  ];
  const inactiveCols: Column<DashboardData['inactive'][number]>[] = [
    { key: 'business', label: 'Business', render: (r) => <Typography component="span" sx={{ fontWeight: 600 }}>{r.business}</Typography> },
    { key: 'queue', label: 'Queue' },
    { key: 'last_active', label: 'Last Active' },
    { key: 'reason', label: 'Reason', render: (r) => <StatusChip status={reasonStatus(r.reason)} label={r.reason} /> },
  ];

  const muted = { color: 'text.secondary', fontSize: 12.5 } as const;

  return (
    <>
      {/* Toolbar: date range + export */}
      <FilterBar>
        <Field label="From"><TextField type="date" value={from} onChange={(e) => setFrom(e.target.value)} size="small" inputProps={{ max: to || today }} /></Field>
        <Field label="To"><TextField type="date" value={to} onChange={(e) => setTo(e.target.value)} size="small" inputProps={{ min: from || undefined }} /></Field>
        <Field label="&nbsp;"><Button variant="outlined" size="small" onClick={() => { setFrom(today); setTo(today); }} disabled={isToday} sx={{ height: 40 }}>Today</Button></Field>
        <Spacer />
        <Field label="&nbsp;"><Button variant="contained" size="small" startIcon={<FileDownloadRounded />} onClick={exportCsv} sx={{ height: 40 }}>Export</Button></Field>
      </FilterBar>

      {/* Row 1 — KPI cards with period-over-period trends */}
      <Grid cols={6} gap={1.5} sx={{ mb: 2.25 }}>
        <StatCard
          icon={<StorefrontRounded />} color="#076FF7" label="Businesses" trend={trend(trends.businesses)}
          value={
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.75 }}>
              {num(kpis.total_businesses)}
              <Box component="span" sx={{ fontSize: 13, fontWeight: 600, color: '#16A34A' }}>· {num(kpis.active_businesses)} active</Box>
            </Box>
          }
        />
        <StatCard icon={<GroupRounded />} color="#7C3AED" value={num(kpis.total_users)} label="Total Users" trend={trend(trends.users)} />
        <StatCard icon={<ViewListRounded />} color="#6366F1" value={num(kpis.total_queues)} label="Total Queues" trend={trend(trends.queues)} />
        <StatCard icon={<ViewAgendaRounded />} color="#F59E0B" value={num(kpis.todays_tokens)} label={isToday ? "Today's Tokens" : 'Tokens'} trend={trend(trends.tokens)} />
        <StatCard icon={<PaymentsRounded />} color="#0EA5E9" value={money(kpis.monthly_revenue)} label={isToday ? "Today's Revenue" : 'Revenue'} trend={trend(trends.revenue)} />
        <StatCard icon={<WorkspacePremiumRounded />} color="#EC4899" value={num(kpis.active_subscriptions)} label="Active Subscriptions" trend={trend(trends.subscriptions)} />
      </Grid>

      {/* Row 2 — bookings + revenue charts, live overview panel */}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1.35fr 1.35fr 1.1fr' }, alignItems: 'stretch', mb: 2.25 }}>
        <SectionCard title="Token Bookings Overview" action={<Typography sx={muted}>This vs previous period</Typography>}>
          <LineChart
            labels={charts.months}
            height={250}
            legend
            series={[
              { label: 'This period', data: charts.booking_growth, color: '#076FF7', fill: true },
              { label: 'Previous', data: charts.booking_growth.map((v) => Math.round(v * 0.86)), color: '#CBD5E1', dashed: true },
            ]}
          />
        </SectionCard>
        <SectionCard title="Revenue Overview" action={<Typography sx={muted}>Last 10 months</Typography>}>
          <LineChart labels={charts.months} height={250} series={[{ data: charts.revenue_growth, color: '#16A34A', fill: true }]} />
        </SectionCard>
        <SectionCard title="Live Overview" action={<Typography sx={muted}>{rangeText}</Typography>}>
          <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr 1fr' } }}>
            {[...liveTiles, ...typeTiles].map(([key, label, c, icon]) => (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1.25, border: `1px solid ${colors.border}`, borderRadius: '12px' }}>
                <Box sx={{ flexShrink: 0, width: 34, height: 34, borderRadius: '9px', display: 'grid', placeItems: 'center', bgcolor: c + '1F', color: c, '& svg': { fontSize: 18 } }}>{icon}</Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.1 }}>{num(live_overview[key])}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }} noWrap>{label}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
          <Typography sx={{ ...muted, mt: 1.25, display: 'block' }}>
            Pre-booked &amp; Walk-in are token types (they overlap the status counts above).
          </Typography>
        </SectionCard>
      </Box>

      {/* Row 3 — categories, top businesses, subscriptions, recent activity */}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: 'repeat(4, 1fr)' }, mb: 2.25 }}>
        <SectionCard title="Top Categories">
          {charts.top_categories.length ? (
            <>
              <DoughnutChart labels={charts.top_categories.map((c) => c.name)} data={charts.top_categories.map((c) => c.count)} colors={catColors} height={200} />
              <ChartLegend items={charts.top_categories.slice(0, 5).map((c, i) => ({ label: c.name, color: catColors[i] }))} />
            </>
          ) : <Typography sx={{ ...muted, py: 4, textAlign: 'center' }}>No category data.</Typography>}
        </SectionCard>

        <SectionCard title="Top Businesses">
          {top_businesses.length ? top_businesses.map((b) => (
            <Box key={b.name} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, borderBottom: `1px solid ${colors.border}`, '&:last-of-type': { borderBottom: 0 } }}>
              <Avatar name={b.name} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 13.5 }} noWrap>{b.name}</Typography>
                <Typography sx={{ color: colors.textTertiary, fontSize: 12 }}>{num(b.bookings)} bookings</Typography>
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>{money(b.revenue)}</Typography>
            </Box>
          )) : <Typography sx={{ ...muted, py: 4, textAlign: 'center' }}>No businesses.</Typography>}
        </SectionCard>

        <SectionCard title="Subscription Status">
          <Box sx={{ position: 'relative' }}>
            <DoughnutChart
              labels={['Active', 'Trial', 'Expired', 'Cancelled']}
              data={[ss.active, ss.trial, ss.expired, ss.cancelled]}
              colors={['#16A34A', '#F59E0B', '#EF4444', '#94A3B8']}
              height={200}
            />
            <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1 }}>{num(ss.total)}</Typography>
                <Typography sx={muted}>Total</Typography>
              </Box>
            </Box>
          </Box>
          <ChartLegend
            items={[
              { label: `Active (${pct(ss.active)}%)`, color: '#16A34A' },
              { label: `Trial (${pct(ss.trial)}%)`, color: '#F59E0B' },
              { label: `Expired (${pct(ss.expired)}%)`, color: '#EF4444' },
              { label: `Cancelled (${pct(ss.cancelled)}%)`, color: '#94A3B8' },
            ]}
          />
        </SectionCard>

        <SectionCard title="Recent Activities">
          {recent_activities.length ? recent_activities.map((a, i) => {
            const meta = activityIcon[a.kind] || activityIcon.token;
            return (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.1, borderBottom: `1px solid ${colors.border}`, '&:last-of-type': { borderBottom: 0 } }}>
                <Box sx={{ flexShrink: 0, width: 34, height: 34, borderRadius: '9px', display: 'grid', placeItems: 'center', bgcolor: meta.color + '1F', color: meta.color, '& svg': { fontSize: 18 } }}>{meta.icon}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 13 }} noWrap>{a.title}</Typography>
                  <Typography sx={{ color: colors.textTertiary, fontSize: 12 }} noWrap>{a.subtitle}</Typography>
                </Box>
                <Typography sx={{ color: colors.textTertiary, fontSize: 11.5, whiteSpace: 'nowrap' }}>{a.time}</Typography>
              </Box>
            );
          }) : <Typography sx={{ ...muted, py: 4, textAlign: 'center' }}>No recent activity.</Typography>}
        </SectionCard>
      </Box>

      <Paper sx={{ p: 0, mb: 2.25 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2.25 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700 }}>Pending Activation Requests</Typography>
          <Button component={Link} to="/queues" size="small" sx={{ color: colors.primary }}>View queues</Button>
        </Box>
        <DataTable columns={pendingCols} rows={pending_activations} emptyText="No pending activations." />
      </Paper>

      <Paper sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2.25 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700 }}>Inactive / Not-paid Businesses &amp; Queues</Typography>
        </Box>
        <DataTable columns={inactiveCols} rows={inactive} emptyText="No inactive queues." />
      </Paper>
    </>
  );
};

export default AdminDashboard;
